import React from 'react'
import { Camera, Tag} from '../../types';

import ImageLoader from 'react-load-image'
import moment from 'moment'

import { Pane, TagInput, Heading, Text, Dialog, toaster, Spinner } from 'evergreen-ui'
import { PlusCircleTwoTone, DeleteTwoTone, EditTwoTone, WarningTwoTone, SmileTwoTone, PauseCircleTwoTone } from '@ant-design/icons'
import { Button, message, Popconfirm, Select, Tooltip } from "antd";

import Datafetcher from '../../utils/datafetcher'
import availableDetectors from '../../utils/availableDetectors'

import TagEdit from '../../components/TagEdit/TagEdit'

import { SelectValue } from 'antd/lib/select'

const times = ["Unpause", "10 minutes", "30 minutes", "1 hour", "5 hours", "1 day", "1 week", "1 month", "3 months"]

const capitalize = (e: string) => e.charAt(0).toUpperCase() + e.slice(1)

const { Option } = Select

interface Props {
    activeCam: Camera | undefined,
    stream: string | undefined,
    tags: Tag[]
    datafetcher: Datafetcher,
    isMobile: boolean,
    refreshCam: boolean,
    refreshInfo: () => void,
    cameraDeleted: () => void,
    showNewCameraModal: () => void,
    changeCamera: (a : Camera | undefined) => void,
}

interface State { 
    activeCam: Camera | undefined,
    stream: string | undefined,
    tags: Tag[]

    isMobile: boolean,
    refreshCam: boolean,

    editingTag: boolean,
    categories: string[],

    selectedPauseTime: SelectValue | null,
    selectedTagPicker: SelectValue | null,
    editedDetector: SelectValue | null,

    displayTagModal: boolean,
    displayPauseModal: boolean,
}

export default class CameraMenu extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props)
        this.state = {
            activeCam: this.props.activeCam,
            stream: this.props.stream,
            tags: this.props.tags,
            isMobile: this.props.isMobile,
            refreshCam: this.props.refreshCam,

            editingTag: false,
            selectedPauseTime: "Unpause",
            categories: availableDetectors.map(capitalize),

            selectedTagPicker: null,
            editedDetector: null,

            displayPauseModal: false,
            displayTagModal: false,
        }
    }

    componentDidUpdate(oldProps:Props) {
        if(this.props === oldProps){return}
        this.setState({
            activeCam: this.props.activeCam,
            stream: this.props.stream,
            tags: this.props.tags,
            isMobile: this.props.isMobile,
            refreshCam: this.props.refreshCam,
        })
    }

    isCameraStable = (camera:Camera) => camera.inferences > 0

    removeCamera(camera: Camera) {
        const { datafetcher, cameraDeleted } = this.props;

        datafetcher.removeCamera(camera.id, (err: Error | null, _: string | null) => {
            if (err) { console.log(err); return }
            cameraDeleted()
            message.info('Camera deleted')
        })
    }

    removeTag = () => {
        const { editedDetector } = this.state;
        const { datafetcher, activeCam, changeCamera, refreshInfo } = this.props;

        if (editedDetector === "" || typeof editedDetector !== "string") {
            toaster.notify('Invalid detector')
            return
        }

        if(!activeCam){return}

        datafetcher.removeTag(activeCam.id, editedDetector.toLowerCase(), (error: Error | null) => {
            if (error) {
                toaster.notify('Something went wrong.')
                return
            }
            refreshInfo()
            this.setState({ editedDetector: "" })
            changeCamera(activeCam)
            toaster.notify('Detector successfully removed.')
        })
    }

    editTag = () => {
        const { editedDetector } = this.state;

        if (editedDetector === "" || editedDetector === undefined || typeof editedDetector !== "string") {
            toaster.notify('Invalid detector')
            return
        }

        let tags = this.getComboboxItems()
        if (!tags) { tags = [] }

        let found = false

        tags.forEach((val) => { if (val.toLowerCase() === editedDetector.toLowerCase()) { found = true } })

        if (!found) {
            toaster.notify('Detector doesn\'t exist.')
            return
        }

        this.setState({
            displayTagModal: true,
            editingTag: true,
        })
    }

    getPauseForTag = () => {
        const { tags } = this.props

        if (tags.length <= 0) { return }
        let cTag : Tag | undefined

        tags.forEach(tag => {
            if (tag.Name === this.state.editedDetector) { cTag = tag }
        });

        if (cTag) {
            const newUnixDate = Date.parse(cTag.Pause)
            if (isNaN(newUnixDate)) { return }
            const nowT = new Date()
            const newDate = new Date(newUnixDate)
            if (cTag.Pause === "" || cTag.Pause === undefined) { return }
            if (newDate <= nowT) { return }
            return "Notifications will restart " + moment(newDate).from(nowT)
        }
    }

    pauseDetector = () => {
        const { editedDetector } = this.state;
        const { datafetcher, activeCam, changeCamera, refreshInfo } = this.props;

        if (editedDetector === "" || editedDetector === undefined || typeof editedDetector !== "string" || typeof this.state.selectedPauseTime !== "string") {
            toaster.notify('Invalid detector')
            return
        }

        if (!activeCam) { return }
        
        const lowerCaseDetector = editedDetector.toLowerCase()
        const timeIndex = times.indexOf(this.state.selectedPauseTime) + 1

        datafetcher.pauseTag(activeCam.id, lowerCaseDetector, timeIndex, (error: Error | null) => {
            if (error) {
                toaster.notify('Something went wrong.')
                return
            }
            refreshInfo()
            changeCamera(activeCam)
            if (this.state.selectedPauseTime === "Unpause") {
                toaster.notify('Notifications will restart shortly.')
            } else {
                toaster.notify('Detector successfully paused for ' + this.state.selectedPauseTime + '.')
            }
        })

        this.hidePauseModal()
    }

    openAddNewTagModal = () => {
        const { activeCam } = this.props;
        const { selectedTagPicker } = this.state;

        if (selectedTagPicker === "" || selectedTagPicker === undefined || typeof selectedTagPicker !== "string") {
            toaster.notify('Invalid detector')
            return
        }

        if (!activeCam) {
            toaster.notify('Please select a connected camera first.')
            return
        }

        let tags = this.getComboboxItems()

        tags.forEach((val) => {
            if (val.toLowerCase() === selectedTagPicker.toLowerCase()) {
                toaster.notify('This detector is already set.')
                return
            }
        })

        this.setState({
            displayTagModal: true,
            editingTag: false,
        })
    }

    showPauseModal = () => {
        const { editedDetector } = this.state;

        if (editedDetector === "" || editedDetector === null) {
            toaster.notify('Please select a detector')
            return
        }

        this.setState({ displayPauseModal: true })
    }

    hidePauseModal = () => {
        this.setState({ displayPauseModal: false })
    }

    closeTagEdit = () => {
        this.setState({ displayTagModal: false })
    }

    getAutoCompleteList () {
        const { categories } = this.state;
        const { tags } = this.props;

        let tagNames = tags.map((tag) => tag.Name)
        return categories.filter((element) => tagNames.indexOf(element) === -1)
    }
    
    getComboboxItems() {
        const { tags } = this.props;
        let tagNames = tags.map((tag) => tag.Name)
        return tagNames
    }

    render = () => {
        const { datafetcher, changeCamera, refreshInfo, showNewCameraModal } = this.props;
        const { isMobile, activeCam, tags, stream, refreshCam, displayTagModal, editingTag, selectedTagPicker, editedDetector } = this.state;

        return <Pane
            elevation={0}
            float="left"
            backgroundColor="white"
            width={isMobile ? "auto" : 400}
            minWidth={isMobile ? "90vw" : 400}
            borderRadius={5}
            height={"fit-content"}
            paddingBottom={30}
            display="flex"
            flexDirection="column"
            overflow="scroll"
            overflowX="hidden">

            {stream ? <TagEdit
                display={displayTagModal}
                activeCam={activeCam}
                tags={tags}
                editingTag={editingTag}
                datafetcher={datafetcher}
                editedDetector={editedDetector}
                selectedTagPicker={selectedTagPicker}
                stream={stream}
                closeMe={this.closeTagEdit}
                refreshInfo={refreshInfo}
                changeCamToSame={() => { changeCamera(activeCam) }}
            ></TagEdit> : <></>}

            <Dialog
                isShown={this.state.displayPauseModal}
                title="Pause detector"
                hasFooter={false}
                onCloseComplete={() => this.setState({ displayPauseModal: false })}
                width={400}>
                <Pane display="flex"
                    alignItems="center"
                    flexDirection="column">
                    <Pane display="flex"
                        alignItems="center"
                        flexDirection="row">
                        <Select
                            showSearch
                            onChange={(changedItem) => this.setState({ selectedPauseTime: changedItem })}
                            placeholder="How long to pause for?"
                            style={{ width: 200 }}
                            optionFilterProp="children"
                            filterOption={(input, option) => {
                                if (!option) { return false }
                                return option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                            }}
                        >
                            {times.map((item) => <Option value={item}>{capitalize(item)}</Option>)}
                        </Select>
                        <Button
                            style={{ marginRight: 10, marginLeft: 20 }}
                            size="small"
                            shape="round"
                            onClick={this.pauseDetector}
                        >
                            <PauseCircleTwoTone />
                        </Button>
                    </Pane>
                </Pane>
            </Dialog>

            {stream ?
                !refreshCam || !stream ?
                    <ImageLoader src={process.env.REACT_APP_SERVER + (stream)} >
                        <img height={300} id="cameraFeed" alt="camera feed" />
                        <div id="camera_display_error">Error!</div>
                        <Pane display="flex" flexDirection="row" justifyContent="center" marginTop={100} marginBottom={100}>
                            <Spinner size={100} />
                        </Pane>
                    </ImageLoader> :
                    <Pane
                        display="flex"
                        flexDirection="row"
                        justifyContent="center"
                        marginTop={100}
                        marginBottom={100}>
                        <Spinner size={100} />
                    </Pane> 
                :<div onClick={() => {showNewCameraModal()}}>
                    <img
                        height={300}
                        width={isMobile ? "auto" : 400}
                        id="cameraFeed"
                        alt="camera feed"
                        src={"https://via.placeholder.com/1200x1200?text=Add+a+Camera."}>
                    </img>
                </div>
            }
            {activeCam ?
                <Pane paddingLeft={20} display="flex" justifyContent="space-between">
                    <Heading id="camera_name" marginTop={15} paddingBottom={15} marginLeft="10">
                        {!this.isCameraStable(activeCam) ?
                            <Tooltip placement="bottom" title={"Your camera's connection might be unstable or some other issue is preventing Safewatch from analyzing the feed. If you just added the camera, it can take a few minutes to establish a stable connection."}>
                                <WarningTwoTone twoToneColor="red" style={{ marginRight: 10 }} />
                            </Tooltip> :
                            <Tooltip placement="bottom" title={"The connection is good and Safewatch is analyzing the footage of the camera in real time."}>
                                <SmileTwoTone twoToneColor="#92c987" style={{ marginRight: 10 }} />
                            </Tooltip>}
                        {activeCam.nickname}
                    </Heading>
                    <Popconfirm
                        title="Are you sure you want to delete this camera?"
                        onConfirm={() => this.removeCamera(activeCam)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button style={{ marginRight: 15, marginTop: 15 }} size="small" danger={true} shape="round">
                            <DeleteTwoTone twoToneColor="red" />
                        </Button>
                    </Popconfirm>
                </Pane>
                : <></>}
            <Pane borderBottom="2px solid #E4E7EB" marginLeft={15} marginRight={15} marginBottom={20}></Pane>
            <Pane
                display="flex"
                flexDirection="row"
                paddingLeft={20}
                paddingRight={10}
                justifyContent="space-between">
                <Text marginTop={5} marginRight={30} color="muted">New detector</Text>
                <Select
                    showSearch
                    onChange={(changedItem) => this.setState({ selectedTagPicker: changedItem })}
                    placeholder="Select a detector"
                    style={{ width: isMobile ? 200 : 180 }}
                    optionFilterProp="children"
                    filterOption={(input, option) => {
                        if (!option) { return false }
                        return option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }}>
                    {this.getAutoCompleteList().map((item) => <Option value={item}>{capitalize(item)}</Option>)}
                </Select>
                <Button
                    style={{ marginRight: 5, marginLeft: 10, marginTop: 5 }}
                    size="small"
                    shape="round"
                    onClick={this.openAddNewTagModal}
                >
                    <PlusCircleTwoTone />
                </Button>
            </Pane>
            <Pane
                display="flex"
                flexDirection="row"
                justifyContent="center">
                <Pane width={25}></Pane>
                <Text marginTop={25} color="muted">Active detectors</Text>
                <TagInput
                    disabled
                    marginTop={20}
                    marginRight={2.5}
                    width={"85%"}
                    values={this.getComboboxItems()}
                />
                <Pane width={15}></Pane>
            </Pane>
            {this.getComboboxItems().length > 0 ? <>
                <Pane
                    display="flex"
                    flexDirection="row"
                    justifyContent="space-between"
                    marginTop={15}>
                    <Select
                        showSearch
                        onChange={selected => this.setState({ editedDetector: selected })}
                        placeholder="Edit Detector"
                        style={{ width: isMobile ? 150 : 200, marginTop: 5, marginLeft: 20 }}
                        optionFilterProp="children"
                        filterOption={(input, option) => {
                            if (!option) { return false } 
                            return option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }}>
                        {this.getComboboxItems().map((item) => <Option value={item}>{capitalize(item)}</Option> )}
                    </Select>
                    <Pane>
                        <Tooltip placement="bottom" title={"Pause notifications on this detector for a period of your choice."}>
                            <Button
                                style={{ marginRight: 10, marginTop: 10, marginLeft: isMobile ? 5 : 10 }}
                                size="small"
                                shape="round"
                                onClick={this.showPauseModal}
                            >
                                <PauseCircleTwoTone />
                            </Button>
                        </Tooltip>
                        <Tooltip placement="bottom" title={"Edit any of the settings for this detector."}>
                            <Button
                                style={{ marginRight: 10, marginTop: 10 }}
                                size="small"
                                shape="round"
                                onClick={this.editTag}
                            >
                                <EditTwoTone />
                            </Button>
                        </Tooltip>
                        <Tooltip placement="bottom" title={"Delete this detector."}>
                            <Button
                                style={{ marginRight: 20, marginTop: 10 }}
                                size="small"
                                shape="round"
                                danger={true}
                                onClick={this.removeTag}
                            >
                                <DeleteTwoTone twoToneColor="red" />
                            </Button>
                        </Tooltip>
                    </Pane>
                </Pane>
                <Pane marginLeft={20} marginTop={20}><i>{this.getPauseForTag()}</i></Pane>
            </> : <></>}
        </Pane>
    }
}